import React, { useRef, useState } from 'react';
import { api } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import { fmtDate } from '../../hooks';
import { Alert, Badge, Button, Card } from '../../ui/basics';
import { Drawer, useToast } from '../../ui/overlay';
import { AddDocRow } from './DocEditor';
import { OfferActions, StatusBadge } from './OfferActions';
import { UPLOAD_TYPES, checkUpload, docDue, docFile, docName } from './docs';

export default function CandidateDetail({ candidate, onClose, onChanged }) {
  const { user } = useAuth();
  const toast = useToast();
  const [row, setRow] = useState(candidate);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const input = useRef(null);
  const target = useRef('');
  const docs = row.required_documents || [];
  const received = docs.filter((d) => docFile(d)).length;

  const apply = (res) => { if (res.data) setRow(res.data); onChanged(); };

  const upload = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setError('');
    const problem = checkUpload(file);
    if (problem) { setError(file.name + ': ' + problem); return; }
    setBusy(target.current);
    const form = new FormData();
    form.append('onboard_id', row.onboard_id);
    form.append('file_type', target.current);
    form.append('file', file);
    try { apply(await api.upload('Update_Onboard_details/', form)); toast(`${target.current} uploaded`, 'success'); }
    catch (err) { setError(err.message); }
    setBusy('');
  };

  const addDoc = async (doc) => {
    setError('');
    try {
      apply(await api.post('Add_Required_Document/', { onboard_id: row.onboard_id, new_file: docName(doc), reminder_time: doc.Time, reminder_details: {} }));
      toast('Document requirement added', 'success');
    } catch (err) { setError(err.message); }
  };

  return (
    <Drawer title={row.candidate_name} onClose={onClose}>
      <div className="stack">
        {error && <Alert>{error}</Alert>}
        <div><StatusBadge row={row} /></div>
        <OfferActions row={row} onResult={apply} />
        <div className="grid cols-2">
          {[['Email', row.candidate_email], ['Phone', row.candidate_phonenumber], ['Client', row.client], ['Facility', row.facility],
            ['Job title', row.job_title], ['Candidate location', row.candidate_location], ['Client location', row.client_location],
            ['Recruiter', row.recruiter], ['Onboarding executive', row.onboarding_executive || (row.user ? row.user + ' (added it)' : '')], ['CC', (row.cc_emails || []).join(', ')], ['Offer date', fmtDate(row.offer_date)], ['Expected start', fmtDate(row.expected_start_date)], ['Added by', row.user], ['Created', fmtDate(row.createdat)]].map(([label, value]) => (
              <div key={label}><div className="muted">{label}</div><b>{value || '—'}</b></div>))}
        </div>
        {row.remarks && <div><div className="muted">Remarks</div><p>{row.remarks}</p></div>}

        <Card title="Documents" subtitle={`${received} of ${docs.length} received`}>
          <input ref={input} type="file" hidden accept={UPLOAD_TYPES.map((t) => '.' + t).join(',')} onChange={upload} />
          <div className="stack" style={{ gap: 8 }}>
            {docs.map((doc) => {
              const name = docName(doc);
              const file = docFile(doc);
              return (
                <div key={name} className="row" style={{ padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 8 }}>
                  <div className="grow"><b>{name}</b>{docDue(doc) && <div className="muted">Due {fmtDate(docDue(doc))}</div>}</div>
                  {file ? <><Badge tone="success">Received</Badge><a href={file} target="_blank" rel="noreferrer">View file</a></> : <Badge tone="warning">Pending</Badge>}
                  {user.can_edit && (
                    <Button size="sm" icon="upload" loading={busy === name} onClick={() => { target.current = name; input.current.click(); }}>
                      {file ? 'Replace' : 'Upload'}
                    </Button>
                  )}
                </div>
              );
            })}
            {user.can_edit && <div style={{ marginTop: 8 }}><AddDocRow onAdd={addDoc} existing={docs.map(docName)} label="Add requirement" /></div>}
          </div>
        </Card>
      </div>
    </Drawer>
  );
}
